/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
    GBVTimelineContainer, GBVTimelineItem, GBVTimelineCard,
    GBVTimelineHeader, GBVTimelineDate, GBVTimelineType,
    GBVTimelineBody, GBVTimelineField, GBVTimelineFieldLabel, GBVTimelineFieldValue,
    GBVTimelineActions, GBVTimelineBtn,
    GBVEmptyState, GBVEmptyIcon, GBVEmptyTitle, GBVEmptyDesc,
    GBVStatusBadge, GBVTagPill, ReferralStatusGrid,
    ConfidentialityBanner, ConfidentialityIcon,
} from "./GBVScreeningStyles";

import {
    GBV_TYPES,
    INJURY_LOCATIONS,
    INJURY_TYPES,
    CLINICAL_MANAGEMENT_ITEMS,
} from "./hooks/useGBVAssessment";

/* ═══════════════════════════════════════════════════════════════
   GBVAssessmentHistory — Timeline view of past GBV assessments
   (Task 20). Shows chronological history with expandable details.
   All records are marked as confidential/restricted.
   ═══════════════════════════════════════════════════════════════ */

const formatDate = function (dateStr) {
    if (!dateStr) return "—";
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });
    } catch (e) {
        return dateStr;
    }
};

const getGBVTypeLabel = function (value) {
    const found = GBV_TYPES.find(function (t) { return t.value === value; });
    return found ? found.label : value;
};

const getGBVTypeIcon = function (value) {
    const found = GBV_TYPES.find(function (t) { return t.value === value; });
    return found ? found.icon : "❓";
};

const getInjuryLocationLabel = function (value) {
    const found = INJURY_LOCATIONS.find(function (l) { return l.value === value; });
    return found ? found.label : value;
};

const getInjuryTypeLabel = function (value) {
    const found = INJURY_TYPES.find(function (t) { return t.value === value; });
    return found ? found.label : value;
};

const getClinicalManagementLabel = function (key) {
    const found = CLINICAL_MANAGEMENT_ITEMS.find(function (item) { return item.key === key; });
    return found ? found.label : key;
};

export const GBVAssessmentHistory = function (props) {
    const gbv = props.gbv;
    const assessments = gbv.assessments;

    const expandedState = useState({});
    const expanded = expandedState[0];
    const setExpanded = expandedState[1];

    const toggleExpand = function (id) {
        setExpanded(function (prev) {
            const updated = Object.assign({}, prev);
            updated[id] = !prev[id];
            return updated;
        });
    };

    /* ─── Empty State ─── */
    if (assessments.length === 0) {
        return (
            <GBVEmptyState>
                <GBVEmptyIcon>🔒</GBVEmptyIcon>
                <GBVEmptyTitle>No GBV Assessments Recorded</GBVEmptyTitle>
                <GBVEmptyDesc>
                    No gender-based violence assessments have been documented for this patient.
                    Use the Assessment tab to record a new confidential assessment.
                </GBVEmptyDesc>
            </GBVEmptyState>
        );
    }

    /* ─── Sort by date descending ─── */
    const sorted = assessments.slice().sort(function (a, b) {
        return new Date(b.screening_date) - new Date(a.screening_date);
    });

    return (
        <div>
            <ConfidentialityBanner>
                <ConfidentialityIcon>🔒</ConfidentialityIcon>
                <div>
                    <strong>Restricted Access Records</strong> — These records are confidential and only
                    accessible to authorized GBV focal persons. Do not share or print without patient consent.
                </div>
            </ConfidentialityBanner>

            <GBVTimelineContainer>
                {sorted.map(function (assessment) {
                    const isOpen = expanded[assessment.id] || false;

                    // Count active referrals
                    let referralCount = 0;
                    if (assessment.police_referral) referralCount++;
                    if (assessment.safe_shelter_referral) referralCount++;
                    if (assessment.legal_aid_referral) referralCount++;
                    if (assessment.psychosocial_referral) referralCount++;

                    // Count clinical interventions
                    let clinicalCount = 0;
                    if (assessment.clinical_management) {
                        Object.keys(assessment.clinical_management).forEach(function (key) {
                            if (assessment.clinical_management[key]) clinicalCount++;
                        });
                    }

                    return (
                        <GBVTimelineItem key={assessment.id} gbvType={assessment.gbv_type}>
                            <GBVTimelineCard>
                                {/* ── Header ── */}
                                <GBVTimelineHeader onClick={function () { toggleExpand(assessment.id); }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                        <GBVTimelineDate>
                                            {formatDate(assessment.screening_date)}
                                        </GBVTimelineDate>
                                        <GBVTimelineType gbvType={assessment.gbv_type}>
                                            {getGBVTypeIcon(assessment.gbv_type)} {getGBVTypeLabel(assessment.gbv_type)}
                                        </GBVTimelineType>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                        <GBVStatusBadge color="#455a64">
                                            Restricted
                                        </GBVStatusBadge>
                                        {assessment.pf3_completed && (
                                            <GBVTagPill color="#e65100">
                                                PF3
                                            </GBVTagPill>
                                        )}
                                        {referralCount > 0 && (
                                            <GBVTagPill color="#0277bd">
                                                {referralCount} Referral{referralCount !== 1 ? "s" : ""}
                                            </GBVTagPill>
                                        )}
                                        {clinicalCount > 0 && (
                                            <GBVTagPill color="#2e7d32">
                                                {clinicalCount} Intervention{clinicalCount !== 1 ? "s" : ""}
                                            </GBVTagPill>
                                        )}
                                        <span style={{ fontSize: "0.9rem", color: "#999" }}>
                                            {isOpen ? "▲" : "▼"}
                                        </span>
                                    </div>
                                </GBVTimelineHeader>

                                {/* ── Body ── */}
                                <GBVTimelineBody open={isOpen}>
                                    {/* Disclosure */}
                                    <div style={{ marginBottom: "12px" }}>
                                        <strong style={{ fontSize: "0.8rem", color: "#455a64", display: "block", marginBottom: "4px" }}>
                                            Disclosure Details
                                        </strong>
                                        <div style={{ fontSize: "0.8rem", color: "#555", padding: "8px 12px", background: "#f5f5f5", borderRadius: "6px", borderLeft: "3px solid #455a64" }}>
                                            {assessment.disclosure_details || "Not recorded"}
                                        </div>
                                    </div>

                                    {/* Injuries */}
                                    {assessment.injuries_documented && assessment.injuries_documented.length > 0 && (
                                        <div style={{ marginBottom: "12px" }}>
                                            <strong style={{ fontSize: "0.8rem", color: "#bf360c", display: "block", marginBottom: "6px" }}>
                                                Injuries Documented ({assessment.injuries_documented.length})
                                            </strong>
                                            {assessment.injuries_documented.map(function (inj, idx) {
                                                return (
                                                    <div key={idx} style={{ fontSize: "0.78rem", padding: "6px 10px", background: "#fff3e0", borderRadius: "4px", marginBottom: "4px" }}>
                                                        <strong>#{idx + 1}</strong> — {getInjuryLocationLabel(inj.location)} — {getInjuryTypeLabel(inj.type)}
                                                        {inj.description && <span style={{ color: "#666" }}> — {inj.description}</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Clinical Management */}
                                    {assessment.clinical_management && (
                                        <div style={{ marginBottom: "12px" }}>
                                            <strong style={{ fontSize: "0.8rem", color: "#1b5e20", display: "block", marginBottom: "6px" }}>
                                                Clinical Management
                                            </strong>
                                            <ReferralStatusGrid>
                                                {Object.keys(assessment.clinical_management).map(function (key) {
                                                    const given = assessment.clinical_management[key];
                                                    return (
                                                        <GBVTagPill key={key} color={given ? "#2e7d32" : "#999"}>
                                                            {getClinicalManagementLabel(key)}
                                                        </GBVTagPill>
                                                    );
                                                })}
                                            </ReferralStatusGrid>
                                        </div>
                                    )}

                                    {/* Referrals */}
                                    <div style={{ marginBottom: "12px" }}>
                                        <strong style={{ fontSize: "0.8rem", color: "#0277bd", display: "block", marginBottom: "6px" }}>
                                            Referrals
                                        </strong>
                                        <ReferralStatusGrid>
                                            <GBVTagPill color={assessment.police_referral ? "#0277bd" : "#999"}>
                                                {assessment.police_referral} Police
                                            </GBVTagPill>
                                            <GBVTagPill color={assessment.safe_shelter_referral ? "#0277bd" : "#999"}>
                                                {assessment.safe_shelter_referral} Safe Shelter
                                            </GBVTagPill>
                                            <GBVTagPill color={assessment.legal_aid_referral ? "#0277bd" : "#999"}>
                                                {assessment.legal_aid_referral} Legal Aid
                                            </GBVTagPill>
                                            <GBVTagPill color={assessment.psychosocial_referral ? "#6a1b9a" : "#999"}>
                                                {assessment.psychosocial_referral} Psychosocial
                                            </GBVTagPill>
                                        </ReferralStatusGrid>
                                        {assessment.psychosocial_referral && (
                                            <div style={{ fontSize: "0.78rem", color: "#555", marginTop: "6px", padding: "6px 10px", background: "#f3e5f5", borderRadius: "4px" }}>
                                                {assessment.psychosocial_referral}
                                            </div>
                                        )}
                                    </div>

                                    {/* Additional Fields */}
                                    <GBVTimelineField>
                                        <GBVTimelineFieldLabel>Consent Obtained:</GBVTimelineFieldLabel>
                                        <GBVTimelineFieldValue>
                                            {assessment.consent_obtained ? "Yes" : "No"}
                                        </GBVTimelineFieldValue>
                                    </GBVTimelineField>
                                    <GBVTimelineField>
                                        <GBVTimelineFieldLabel>Safety Plan Discussed:</GBVTimelineFieldLabel>
                                        <GBVTimelineFieldValue>
                                            {assessment.safety_plan_discussed ? "Yes" : "No"}
                                        </GBVTimelineFieldValue>
                                    </GBVTimelineField>
                                    <GBVTimelineField>
                                        <GBVTimelineFieldLabel>PF3 Completed:</GBVTimelineFieldLabel>
                                        <GBVTimelineFieldValue>
                                            {assessment.pf3_completed ? "Yes" : "No"}
                                        </GBVTimelineFieldValue>
                                    </GBVTimelineField>
                                    <GBVTimelineField>
                                        <GBVTimelineFieldLabel>Follow-up Date:</GBVTimelineFieldLabel>
                                        <GBVTimelineFieldValue>
                                            {assessment.follow_up_date ? formatDate(assessment.follow_up_date) : "Not scheduled"}
                                        </GBVTimelineFieldValue>
                                    </GBVTimelineField>
                                    <GBVTimelineField>
                                        <GBVTimelineFieldLabel>Assessed By:</GBVTimelineFieldLabel>
                                        <GBVTimelineFieldValue>{assessment.assessed_by || "—"}</GBVTimelineFieldValue>
                                    </GBVTimelineField>

                                    {/* Actions */}
                                    <GBVTimelineActions>
                                        <GBVTimelineBtn
                                            color="#455a64"
                                            onClick={function () { gbv.startEdit(assessment); }}
                                        >
                                            Edit Assessment
                                        </GBVTimelineBtn>
                                    </GBVTimelineActions>
                                </GBVTimelineBody>
                            </GBVTimelineCard>
                        </GBVTimelineItem>
                    );
                })}
            </GBVTimelineContainer>
        </div>
    );
};
